import { useReadQuery } from '@apollo/client'
import { use, useEffect, useMemo } from 'react'
import { setEventTrackingSuppressed } from '@/components/mixpanel/client'
import { QueryRefsContext } from '@/components/root/query-refs-context'
import { useIdCompany, useMixpanel, useRBAC } from '@/lib/hooks'

/**
 * Effect-only component that syncs the current user into Mixpanel. Rendered
 * below RBACProvider so it can read the superadmin flag, and below
 * MixpanelProvider so useMixpanel() is in scope. Reuses the shared user query
 * ref (no extra network request).
 */
export const MixpanelIdentifier = () => {
  const { idCompany } = useIdCompany()
  const { rbac } = useRBAC()
  const { userCompaniesQueryRef } = use(QueryRefsContext)
  const { data } = useReadQuery(userCompaniesQueryRef)
  const { identify, identifyCompany, register, setUserProperties } =
    useMixpanel()

  const user = useMemo(() => data?.user ?? null, [data])
  const companies = useMemo(() => data?.user?.companies ?? [], [data])
  const selectedCompany = useMemo(
    () => companies.find((c) => c.id === idCompany) ?? null,
    [companies, idCompany]
  )
  const isKinstaSuperadmin = useMemo(
    () => rbac.kinsta().superadmin.can(),
    [rbac]
  )

  useEffect(() => {
    if (!user?.id) {
      return
    }

    // Kinsta employees are still identified (profile + isKinstaEmployee flag),
    // but their actions must not count in product metrics — suppress track().
    setEventTrackingSuppressed(isKinstaSuperadmin)

    identify(user.id)
    setUserProperties({
      $email: user.email ?? '',
      $name: user.fullName ?? '',
      isKinstaEmployee: isKinstaSuperadmin,
    })
    register({
      isKinstaEmployee: isKinstaSuperadmin,
      // `company_id` is managed by set_group in identifyCompany; keep the
      // readable name here for quick event breakdowns.
      ...(selectedCompany ? { company_name: selectedCompany.name } : {}),
    })

    if (selectedCompany) {
      identifyCompany({
        idCompany: selectedCompany.id,
        name: selectedCompany.name,
      })
    }
  }, [
    user?.id,
    user?.email,
    user?.fullName,
    selectedCompany,
    isKinstaSuperadmin,
    identify,
    identifyCompany,
    register,
    setUserProperties,
  ])

  return null
}
